import { spawn } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'
import { stripVTControlCharacters } from 'node:util'

import { parseDotenvFile, pullVercelEnvValue } from './env-pull.js'
import { runVercel } from './runner.js'
import type { VercelDeployResult, VercelRunner } from './types.js'

/** Production builds run remotely; give the deploy a long window. */
const DEPLOY_TIMEOUT_MS = 900_000
const ENV_ADD_TIMEOUT_MS = 30_000
/** Failed deploys can carry full build logs; only the tail is useful inline. */
const DETAIL_MAX_LINES = 15

/**
 * Keys from `.env.local` that must never be pushed to the Vercel project:
 * - `VERCEL_OIDC_TOKEN` is Vercel-owned link state, not app config.
 * - `BETTERSTART_AUTH_URL` / `NEXT_PUBLIC_BETTERSTART_AUTH_URL` hold the local
 *   dev origin (`http://localhost:<port>`); in production better-auth derives
 *   the base URL from the request and the client falls back to
 *   `window.location.origin`, so pushing the localhost value would break auth.
 */
const ENV_SYNC_SKIP_KEYS = new Set([
  'VERCEL_OIDC_TOKEN',
  'BETTERSTART_AUTH_URL',
  'NEXT_PUBLIC_BETTERSTART_AUTH_URL'
])

export interface DeployEnvVar {
  key: string
  value: string
}

export interface EnvSyncResult {
  synced: string[]
  failed: string[]
}

export interface VercelDeployOptions {
  env?: NodeJS.ProcessEnv
  /** Receives each non-empty Vercel stdout/stderr line while the deploy runs. */
  onLogLine?: (line: string) => void
}

/** True for values that are scaffold placeholders rather than real config. */
function isPlaceholderValue(value: string): boolean {
  return value === 'postgresql://...' || value.startsWith('your_')
}

/**
 * Collect the env vars worth pushing to the Vercel project's production
 * environment: every non-empty, non-placeholder `.env.local` value except the
 * skip-listed keys and anything already present in production. Existing
 * production values are never overwritten — a connected resource or local env
 * sync can provide `DATABASE_URL`, and a connected Blob store manages
 * `BLOB_READ_WRITE_TOKEN` on the project itself.
 */
export function collectDeployEnvVars(
  cwd: string,
  existingProductionKeys: Set<string>
): DeployEnvVar[] {
  const local = parseDotenvFile(path.join(cwd, '.env.local'))
  const vars: DeployEnvVar[] = []

  for (const [key, value] of local) {
    if (ENV_SYNC_SKIP_KEYS.has(key)) continue
    if (!value || isPlaceholderValue(value)) continue
    if (existingProductionKeys.has(key)) continue
    vars.push({ key, value })
  }

  return vars
}

/**
 * Push the missing production env vars to the linked Vercel project before
 * deploying. Reads the project's current production environment through a temp
 * `vercel env pull` (never touching `.env.local`), then adds each missing key
 * with `vercel env add <key> production`, feeding the value over stdin. A pull
 * failure degrades to "assume nothing exists" — per-key `env add` failures
 * (e.g. a key that does exist after all) are collected, never thrown.
 */
export async function syncVercelProductionEnv(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<EnvSyncResult> {
  const existingKeys =
    (await pullVercelEnvValue(
      runner,
      cwd,
      (filePath) => new Set(parseDotenvFile(filePath).keys()),
      env,
      'production'
    )) ?? new Set<string>()

  const synced: string[] = []
  const failed: string[] = []

  for (const { key, value } of collectDeployEnvVars(cwd, existingKeys)) {
    const add = await runVercel(runner, ['env', 'add', key, 'production'], {
      cwd,
      mode: 'capture',
      timeoutMs: ENV_ADD_TIMEOUT_MS,
      env,
      stdinInput: `${value}\n`
    })

    if (add.success) synced.push(key)
    else failed.push(key)
  }

  return { synced, failed }
}

/** Dependency specs that only resolve inside a local checkout/workspace. */
const LOCAL_PROTOCOL_PATTERN = /^(workspace|link|file|portal):/

/** The `import { defineConfig } from 'betterstart-cli'` line in admin.config.ts. */
const ADMIN_CONFIG_IMPORT_PATTERN =
  /^import\s+\{[^}]*\bdefineConfig\b[^}]*\}\s+from\s+['"]betterstart-cli['"];?[^\S\n]*$/m

/**
 * Local identity shim that replaces the betterstart-cli import for the deploy
 * window. Keeps `admin.config.ts` valid TypeScript (Next's build type checks
 * every file in the tsconfig include set) and still loadable by the CLI even
 * if the restore never runs — `loadConfigFile` only reads the default export.
 */
const ADMIN_CONFIG_IMPORT_SHIM = 'const defineConfig = <T>(config: T): T => config'

export type LockfileGuardStatus = 'updated' | 'failed' | 'not-needed'

/** Regenerates the lockfile at `cwd` to match `package.json`; true on success. */
export type LockfileRefresher = (cwd: string) => Promise<boolean>

export interface ProjectDeployGuard {
  /** Deps (other than betterstart-cli) whose local-protocol specs cannot install on Vercel. */
  localSpecDeps: string[]
  /** Whether pnpm-lock.yaml was regenerated to match the stripped package.json. */
  lockfile: LockfileGuardStatus
  /** Put the original files back, byte for byte. Safe to call when nothing changed. */
  restore(): void
}

/**
 * Make the uploaded project buildable on Vercel's build machine for the
 * duration of `vercel deploy`:
 * - The `betterstart-cli` entry is stripped from `package.json`
 *   `dependencies`/`devDependencies`: the deployed app never imports the CLI at
 *   runtime, and its dev spec — `workspace:*` in a monorepo checkout, or a
 *   not-yet-published version — makes the remote `npm install` exit 1 with
 *   `EUNSUPPORTEDPROTOCOL`/404 before the build even starts.
 * - When that stripping changed `package.json` and the project has its own
 *   `pnpm-lock.yaml`, the lockfile is regenerated with
 *   `pnpm install --lockfile-only`: Vercel's CI pnpm defaults to
 *   `--frozen-lockfile`, so an uploaded lockfile that still lists
 *   `betterstart-cli` kills the remote install with
 *   `ERR_PNPM_OUTDATED_LOCKFILE` before the build starts.
 * - The `defineConfig` import in `admin.config.ts` (the only generated file
 *   that imports `betterstart-cli`, and CLI-only state the deployed app never
 *   reads) is swapped for a local identity shim so `next build`'s type check
 *   passes without the package.
 * Other deps that still use local protocols are reported (for a warning) but
 * never modified — they are user-owned and the CLI cannot know a safe
 * substitute.
 *
 * `restore()` writes the original files back verbatim; call it in a `finally`
 * once the deploy command returns (the remote build reads the uploaded copies).
 */
export async function guardProjectForDeploy(
  cwd: string,
  refreshLockfile: LockfileRefresher = runPnpmLockfileOnly
): Promise<ProjectDeployGuard> {
  const restores: Array<() => void> = []
  const { localSpecDeps, removedCliDep } = guardPackageJson(cwd, restores)
  guardAdminConfig(cwd, restores)
  const lockfile = removedCliDep
    ? await guardPnpmLockfile(cwd, restores, refreshLockfile)
    : 'not-needed'

  return {
    localSpecDeps,
    lockfile,
    restore() {
      for (const restoreFile of restores) {
        try {
          restoreFile()
        } catch {
          // best effort — never let a restore abort the deploy result
        }
      }
    }
  }
}

interface PackageJsonGuardResult {
  localSpecDeps: string[]
  removedCliDep: boolean
}

function guardPackageJson(cwd: string, restores: Array<() => void>): PackageJsonGuardResult {
  const packageJsonPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(packageJsonPath)) {
    return { localSpecDeps: [], removedCliDep: false }
  }

  const original = fs.readFileSync(packageJsonPath, 'utf-8')
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(original) as Record<string, unknown>
  } catch {
    return { localSpecDeps: [], removedCliDep: false }
  }

  const localSpecDeps: string[] = []
  let removed = false

  for (const section of ['dependencies', 'devDependencies'] as const) {
    const deps = parsed[section]
    if (!deps || typeof deps !== 'object') continue
    const record = deps as Record<string, unknown>

    if ('betterstart-cli' in record) {
      Reflect.deleteProperty(record, 'betterstart-cli')
      removed = true
    }

    for (const [name, spec] of Object.entries(record)) {
      if (typeof spec === 'string' && LOCAL_PROTOCOL_PATTERN.test(spec)) {
        localSpecDeps.push(name)
      }
    }
  }

  if (removed) {
    fs.writeFileSync(packageJsonPath, `${JSON.stringify(parsed, null, 2)}\n`, 'utf-8')
    restores.push(() => fs.writeFileSync(packageJsonPath, original, 'utf-8'))
  }

  return { localSpecDeps, removedCliDep: removed }
}

/**
 * Regenerate the project's own `pnpm-lock.yaml` against the stripped
 * `package.json` for the deploy window. Skipped when the project has no
 * lockfile at `cwd` (npm/yarn projects, or a monorepo member whose lockfile
 * lives at the workspace root and is never uploaded). The restore is
 * registered before the refresh runs so even a partial write is rolled back.
 */
async function guardPnpmLockfile(
  cwd: string,
  restores: Array<() => void>,
  refreshLockfile: LockfileRefresher
): Promise<LockfileGuardStatus> {
  const lockfilePath = path.join(cwd, 'pnpm-lock.yaml')
  if (!fs.existsSync(lockfilePath)) {
    return 'not-needed'
  }

  const original = fs.readFileSync(lockfilePath, 'utf-8')
  restores.push(() => fs.writeFileSync(lockfilePath, original, 'utf-8'))

  return (await refreshLockfile(cwd)) ? 'updated' : 'failed'
}

/** Lockfile resolution hits the registry; allow a generous window. */
const LOCKFILE_REFRESH_TIMEOUT_MS = 120_000

function runPnpmLockfileOnly(cwd: string): Promise<boolean> {
  return new Promise((resolve) => {
    const child = spawn('pnpm', ['install', '--lockfile-only'], { cwd, stdio: 'ignore' })
    const timer = setTimeout(() => {
      child.kill('SIGKILL')
      resolve(false)
    }, LOCKFILE_REFRESH_TIMEOUT_MS)
    child.on('close', (code) => {
      clearTimeout(timer)
      resolve(code === 0)
    })
    child.on('error', () => {
      clearTimeout(timer)
      resolve(false)
    })
  })
}

function guardAdminConfig(cwd: string, restores: Array<() => void>): void {
  const adminConfigPath = path.join(cwd, 'admin.config.ts')
  if (!fs.existsSync(adminConfigPath)) {
    return
  }

  const original = fs.readFileSync(adminConfigPath, 'utf-8')
  if (!ADMIN_CONFIG_IMPORT_PATTERN.test(original)) {
    return
  }

  fs.writeFileSync(
    adminConfigPath,
    original.replace(ADMIN_CONFIG_IMPORT_PATTERN, ADMIN_CONFIG_IMPORT_SHIM),
    'utf-8'
  )
  restores.push(() => fs.writeFileSync(adminConfigPath, original, 'utf-8'))
}

/**
 * Make sure the project deploys with the Next.js framework preset. The flow
 * may create the Vercel project with `vercel projects add` during the deploy
 * step (required for the name-conflict probing), which leaves the project's
 * Framework Preset as "Other" — the build then ignores the Next.js output and
 * every route 404s.
 * A `vercel.json` with `framework: "nextjs"` fixes the preset per deployment,
 * so write one when the project has none. An existing `vercel.json` is
 * user-owned and never modified. The file is intentionally permanent (not
 * guard-restored): it is correct project config for any future deploy.
 */
export function ensureVercelJsonFramework(cwd: string): 'created' | 'exists' {
  const vercelJsonPath = path.join(cwd, 'vercel.json')
  if (fs.existsSync(vercelJsonPath)) {
    return 'exists'
  }

  fs.writeFileSync(
    vercelJsonPath,
    `${JSON.stringify(
      { $schema: 'https://openapi.vercel.sh/vercel.json', framework: 'nextjs' },
      null,
      2
    )}\n`,
    'utf-8'
  )
  return 'created'
}

/**
 * Extract the deployed URL from `vercel deploy --prod` output. Newer CLIs print
 * a `Production: https://…` line (the canonical domain) to stderr; stdout is
 * the raw deployment URL. Prefer the production alias, fall back to stdout.
 */
export function parseDeployedUrl(stdout: string, stderr: string): string | undefined {
  const plainErr = stripVTControlCharacters(stderr)
  const production = plainErr.match(/Production:\s+(https:\/\/\S+)/i)?.[1]
  if (production) return production

  const fromStdout = stripVTControlCharacters(stdout).match(/https:\/\/\S+/)?.[0]
  return fromStdout
}

/**
 * Create a production deployment of the linked project with
 * `vercel deploy --prod --yes` (captured output with optional per-line progress,
 * long timeout — the build runs on Vercel). Never throws; failures return a
 * reason plus the output tail.
 */
export async function deployVercelProject(
  runner: VercelRunner,
  cwd: string,
  options: VercelDeployOptions = {}
): Promise<VercelDeployResult> {
  const deploy = await runVercel(runner, ['deploy', '--prod', '--yes'], {
    cwd,
    mode: 'capture',
    timeoutMs: DEPLOY_TIMEOUT_MS,
    env: options.env,
    onOutputLine: options.onLogLine
  })

  if (deploy.success) {
    return { url: parseDeployedUrl(deploy.stdout, deploy.stderr) }
  }

  return {
    failure: deploy.timedOut ? 'timeout' : 'deploy-failed',
    detail: outputTail(`${deploy.stdout}\n${deploy.stderr}`)
  }
}

/** Last non-empty lines of captured output, for inline failure detail. */
function outputTail(output: string): string | undefined {
  const lines = stripVTControlCharacters(output)
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
  if (lines.length === 0) return undefined
  return lines.slice(-DETAIL_MAX_LINES).join('\n')
}
